import { ArrowIcon, Box, Button } from '@modusign/design-system';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useScroll, useWindowSize } from 'react-use';

import './TabBar.scss';

interface TabBarProps {
  children: ReactNode;
}

const SCROLL_BUTTON_VISIBLE_OFFSET = 20;
const SCROLL_MOVE_OFFSET = 50;

export default function TabBar({ children }: TabBarProps) {
  const [clientWidth, setClientWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const ulRef = useRef<HTMLUListElement>(null);
  const { x } = useScroll(ulRef);
  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (ulRef.current) {
      setClientWidth(ulRef.current.clientWidth);
      setScrollWidth(ulRef.current.scrollWidth);
    }
  }, [windowWidth]);

  const hasScrollBar = scrollWidth > clientWidth;
  const maxScrollX = scrollWidth - clientWidth;

  function handleScrollToLeft() {
    if (ulRef.current) {
      ulRef.current.scrollTo(x - SCROLL_MOVE_OFFSET, 0);
    }
  }

  function handleScrollToRight() {
    if (ulRef.current) {
      ulRef.current.scrollTo(x + SCROLL_MOVE_OFFSET, 0);
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderBottomColor: 'lightGray300',
        height: 55,
        position: 'relative',
        width: '100%',
      }}
    >
      <Box
        as="ul"
        className="TabBar"
        ref={ulRef}
        role="tablist"
        sx={{
          fontSize: 15,
          margin: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: 0,
          scrollbarWidth: 'none',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {children}
      </Box>
      {hasScrollBar && x > SCROLL_BUTTON_VISIBLE_OFFSET && (
        <Button
          height="100%"
          m={0}
          p={0}
          sx={{
            borderBottom: 'none',
            borderRadius: 0,
            borderTop: 'none',
            boxShadow: 'none',
            left: 0,
            opacity: 1,
            position: 'absolute',
            top: 0,
          }}
          width="30px"
          onClick={handleScrollToLeft}
        >
          <ArrowIcon direction="left" />
        </Button>
      )}
      {hasScrollBar && x < maxScrollX - SCROLL_BUTTON_VISIBLE_OFFSET && (
        <Button
          height="100%"
          m={0}
          p={0}
          sx={{
            borderBottom: 'none',
            borderRadius: 0,
            borderTop: 'none',
            boxShadow: 'none',
            position: 'absolute',
            right: 0,
            top: 0,
          }}
          width="30px"
          onClick={handleScrollToRight}
        >
          <ArrowIcon direction="right" />
        </Button>
      )}
    </Box>
  );
}
